import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AdminLayout } from './components/AdminLayout';
import { useAuthStore } from './store/authStore';

const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const UserDetailPage = lazy(() => import('./pages/UserDetailPage'));
const SubscriptionsPage = lazy(() => import('./pages/SubscriptionsPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const SystemPage = lazy(() => import('./pages/SystemPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));

function LoadingFallback() {
  return (
    <div className="page-loader">
      <div className="spinner spinner--lg" />
      <p className="page-loader__text">Loading...</p>
    </div>
  );
}

function AdminGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'admin' && user.role !== 'super_admin') {
    return (
      <div className="admin-guard-denied">
        <div className="admin-guard-denied__card">
          <h1>Access Denied</h1>
          <p>You do not have admin privileges to access this panel.</p>
          <p className="admin-guard-denied__role">
            Current role: <code>{user.role}</code>
          </p>
          <a href="/" className="admin-guard-denied__link">
            Return Home
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export function App() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          element={
            <AdminGuard>
              <AdminLayout />
            </AdminGuard>
          }
        >
          <Route path="/" element={<DashboardPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/users/:id" element={<UserDetailPage />} />
          <Route path="/subscriptions" element={<SubscriptionsPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/system" element={<SystemPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
